/* ============================================================================
 * Root providers
 * ----------------------------------------------------------------------------
 * Provider order matters: QueryClientProvider must sit inside WagmiProvider so
 * wagmi's hooks can reach the query client.
 *
 * `reconnectOnMount={false}` is deliberate and load-bearing: with it enabled,
 * wagmi silently restores the stored connector on first paint and the wallet
 * extension throws a popup before the user has clicked anything. Identity is
 * opt-in here — see src/hooks/useWallet.ts.
 * ========================================================================= */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider } from 'wagmi';
import App from './App';
import { wagmiConfig } from './lib/wagmi';
import { AppearanceProvider } from './components/AppearanceProvider';
import './styles/tokens.css';
import './styles/desktop.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Chain reads are cached briefly; ENS/balances are cheap but not free.
      staleTime: 20_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing from index.html');

createRoot(rootEl).render(
  <StrictMode>
    <WagmiProvider config={wagmiConfig} reconnectOnMount={false}>
      <QueryClientProvider client={queryClient}>
        <AppearanceProvider>
          <App />
        </AppearanceProvider>
      </QueryClientProvider>
    </WagmiProvider>
  </StrictMode>,
);
